import React, { useRef, useState } from "react";
import { G, UI, useStore, useIsGM, controlsObj } from "../store";
import { setGame } from "../net";
import type { TurnEntry } from "../../shared/types";
import { prompt, uid } from "../util";

export function TurnTracker() {
  const open = useStore(UI, s => s.turnOpen);
  const game = useStore(G, s => s.game);
  const objs = useStore(G, s => s.recs.obj);
  const gm = useIsGM();
  const [pos, setPos] = useState({ x: 70, y: 70 });
  if (!open || !game) return null;
  const turns = game.state.turns;
  const visible = turns.filter(t => t.custom || gm || (t.tokenId && objs[t.tokenId]));

  const setTurns = (t: TurnEntry[], extra: any = {}) => setGame({ turns: t, ...extra });
  const next = () => {
    if (!turns.length) return;
    const rotated = [...turns.slice(1), turns[0]];
    // a new round starts when the highest initiative comes back to the top
    const maxInit = Math.max(...turns.map(t => parseFloat(t.init) || -Infinity));
    const wrapped = parseFloat(rotated[0].init) === maxInit;
    if (gm) setTurns(rotated, wrapped ? { turnRound: game.state.turnRound + 1 } : {});
    else setGame({ turns: rotated, ...(wrapped ? { turnRound: game.state.turnRound + 1 } : {}) });
    const tok = rotated[0].tokenId ? objs[rotated[0].tokenId] : null;
    if (tok) window.dispatchEvent(new CustomEvent("hearth:center", { detail: { x: tok.x + tok.w / 2, y: tok.y + tok.h / 2 } }));
  };
  const prev = () => gm && turns.length && setTurns([turns[turns.length - 1], ...turns.slice(0, -1)]);
  const sort = () => setTurns([...turns].sort((a, b) => (parseFloat(b.init) || 0) - (parseFloat(a.init) || 0)), { turnRound: 1 });
  const cur = turns[0];
  const curTok = cur?.tokenId ? objs[cur.tokenId] : null;
  const myTurn = !!curTok && controlsObj(curTok);

  const drag = (e: React.PointerEvent) => {
    const sx = e.clientX - pos.x, sy = e.clientY - pos.y;
    const move = (ev: PointerEvent) => setPos({ x: ev.clientX - sx, y: Math.max(0, ev.clientY - sy) });
    const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  return (
    <div className="turns" style={{ left: pos.x, top: pos.y }}>
      <div className="win-title" onPointerDown={drag}>
        <span>Turn order · Round {game.state.turnRound}</span>
        <button onPointerDown={e => e.stopPropagation()} onClick={() => UI.set({ turnOpen: false })}>✕</button>
      </div>
      <div className="turn-list">
        {visible.map((t, i) => {
          const o = t.tokenId ? objs[t.tokenId] : null;
          const name = t.custom ? t.name : o ? (o.name || "(token)") : "(hidden)";
          const canEdit = gm || (o && controlsObj(o));
          return (
            <div
              key={t.id}
              className={`turn${t === cur ? " current" : ""}${o?.layer === "gm" ? " gmonly" : ""}`}
              onClick={() => o && window.dispatchEvent(new CustomEvent("hearth:center", { detail: { x: o.x + o.w / 2, y: o.y + o.h / 2 } }))}
            >
              {o?.src ? <img src={o.src} alt="" /> : <span className="turn-ic">{t.custom ? "⏲" : "●"}</span>}
              <span className="grow">{name}</span>
              {canEdit ? (
                <input className="turn-init" value={t.init} onClick={e => e.stopPropagation()} onChange={e => setTurns(turns.map(x => (x.id === t.id ? { ...x, init: e.target.value } : x)))} />
              ) : <span className="turn-init">{t.init}</span>}
              {gm && <button className="mini" onClick={e => { e.stopPropagation(); setTurns(turns.filter(x => x.id !== t.id)); }}>✕</button>}
            </div>
          );
        })}
        {!visible.length && <div className="dim pad small">Right-click tokens → “Add to turn order”, or roll initiative from a sheet with the token selected.</div>}
      </div>
      <div className="turn-bar">
        {gm && <button onClick={prev} title="Previous turn">◀</button>}
        {(gm || myTurn) && <button className="primary" onClick={next}>{gm ? "Next turn ▶" : "End my turn ▶"}</button>}
        {gm && <button onClick={sort} title="Sort by initiative, descending">Sort</button>}
        {gm && <button onClick={async () => {
          const name = await prompt("Custom entry (e.g. Lair action, Round counter)");
          if (!name) return;
          const init = (await prompt("Initiative value", "20")) ?? "0";
          setTurns([...turns, { id: uid(), name, init, custom: true }]);
        }} title="Add a custom entry">＋</button>}
        {gm && <button onClick={() => setTurns([], { turnRound: 1 })} title="Clear">Clear</button>}
      </div>
    </div>
  );
}
